import React, { useState } from "react";
import { Building2, Image as ImageIcon, ExternalLink, Save, Users } from "lucide-react";
import { api } from "../lib/api";

/**
 * BusinessProfilePanel — Business Concierge profile management card.
 *
 * Shown ONLY for accounts where provider.account_type === "business". Lets
 * the business owner edit their business name, logo, cover image, years of
 * experience, service areas, and website. Includes a "View Public Profile"
 * deep link.
 *
 * Trust badges + verification status flow through the existing identity /
 * Elite / Certified components and are NOT editable here — keeps the
 * "earned trust vs purchased growth" model intact.
 *
 * The parent should pass `key={provider.user_id}` so React remounts this
 * component when the provider record swaps (avoids stale form state without
 * needing a setState-in-effect pattern).
 */
export default function BusinessProfilePanel({ provider, onSaved }) {
  const [form, setForm] = useState(() => ({
    business_name: provider?.business_name || "",
    logo_url: provider?.logo_url || "",
    cover_image: provider?.cover_image || "",
    years_experience: provider?.years_experience || 0,
    service_areas: (provider?.service_areas || []).join(", "),
    website: provider?.website || "",
    bio: provider?.bio || "",
  }));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setBusy(true); setSaved(false);
    try {
      await api.post("/providers/update", {
        business_name: form.business_name,
        logo_url: form.logo_url,
        cover_image: form.cover_image,
        years_experience: Number(form.years_experience) || 0,
        service_areas: form.service_areas.split(",").map((s) => s.trim()).filter(Boolean),
        website: form.website,
        bio: form.bio,
      });
      setSaved(true);
      onSaved?.();
    } catch (e) {
      // Preview/permission errors surface via the global axios interceptor.
      console.error("Failed to save business profile", e);
    }
    setBusy(false);
    setTimeout(() => setSaved(false), 3000);
  };

  // Slug for the public profile link — falls back to business_name → name.
  const slug = (form.business_name || provider?.business_name || "concierge")
    .toLowerCase()
    .split(/\s+/)
    .map((p) => p.replace(/[^a-z0-9]/g, ""))
    .filter(Boolean)
    .join("-");

  return (
    <section
      className="mt-12 rounded-3xl card-luxury-elevated overflow-hidden"
      data-testid="business-profile-panel"
    >
      <div className="p-6 border-b border-[#071A2D]/8 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold flex items-center gap-1.5">
            <Building2 size={12}/> Business Profile
          </div>
          <h2 className="font-serif text-2xl mt-1.5">Manage your business</h2>
          <p className="text-xs text-[#071A2D]/60 mt-1">
            Business info, logo, portfolio, and service areas. Customers see this on your public profile.
          </p>
        </div>
        <a
          href={`/concierge/${slug}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="business-view-public-profile"
          className="btn-outline-emerald text-xs inline-flex items-center gap-2"
        >
          View Public Profile <ExternalLink size={12}/>
        </a>
      </div>
      <div className="p-6 grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Business name</label>
          <input
            data-testid="biz-name"
            value={form.business_name}
            onChange={(e) => setForm({ ...form, business_name: e.target.value })}
            className="kbd-input"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Years of experience</label>
          <input
            data-testid="biz-years"
            type="number"
            min="0"
            value={form.years_experience}
            onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
            className="kbd-input"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Business logo URL</label>
          <input
            data-testid="biz-logo"
            placeholder="https://…"
            value={form.logo_url}
            onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
            className="kbd-input"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Cover image URL</label>
          <input
            data-testid="biz-cover"
            placeholder="https://…"
            value={form.cover_image}
            onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
            className="kbd-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Service areas (comma-separated)</label>
          <input
            data-testid="biz-service-areas"
            placeholder="e.g. Washington D.C., Maryland, Virginia"
            value={form.service_areas}
            onChange={(e) => setForm({ ...form, service_areas: e.target.value })}
            className="kbd-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Website</label>
          <input
            data-testid="biz-website"
            placeholder="https://…"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            className="kbd-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Business bio / description</label>
          <textarea
            data-testid="biz-bio"
            rows="3"
            placeholder="One paragraph that introduces your business to customers."
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            className="kbd-input"
          />
        </div>
      </div>

      {/* Image preview row */}
      {(form.cover_image || form.logo_url) && (
        <div className="px-6 pb-6">
          <div className="text-[11px] uppercase tracking-widest text-[#064E3B]/70 mb-2 flex items-center gap-1.5">
            <ImageIcon size={11}/> Live preview
          </div>
          <div className="rounded-2xl card-luxury-well overflow-hidden">
            {form.cover_image && (
              <img src={form.cover_image} alt="Business cover" className="w-full h-32 object-cover"/>
            )}
            <div className="p-4 flex items-center gap-4">
              {form.logo_url && (
                <img src={form.logo_url} alt="Logo" className="w-14 h-14 rounded-2xl object-cover border border-[#071A2D]/10"/>
              )}
              <div>
                <div className="font-serif text-lg">{form.business_name || "Your business name"}</div>
                <div className="text-xs text-[#071A2D]/60">
                  {form.years_experience ? `${form.years_experience} yrs experience` : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 pb-6 flex items-center justify-end gap-3">
        {saved && <span className="text-xs text-[#064E3B]" data-testid="biz-saved">Saved ✓</span>}
        <button
          onClick={save}
          disabled={busy}
          data-testid="biz-save"
          className="btn-coral inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Save size={14}/> {busy ? "Saving…" : "Save business profile"}
        </button>
      </div>

      {/* Team foundation — placeholder for future team management */}
      <div className="px-6 pb-6 pt-4 border-t border-[#071A2D]/8" data-testid="business-team-section">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold flex items-center gap-1.5">
          <Users size={12}/> Team
        </div>
        <div className="mt-3 rounded-2xl card-luxury-well p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#064E3B] text-[#FFF8EE] flex items-center justify-center text-sm font-semibold">
            {(provider?.business_name || "B").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#071A2D]">Business Owner</div>
            <div className="text-xs text-[#071A2D]/60">You · Full account access</div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#C9A45C] font-semibold">Owner</span>
        </div>
        <p className="text-[11px] text-[#071A2D]/55 mt-2 italic">
          Invite team members coming soon. We&apos;re preparing scoped permissions + experience assignment per teammate.
        </p>
      </div>
    </section>
  );
}
